import { ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { RanaLoading, RanaPageTitle } from "@nens/ranalib";

import { RanaPageTransition } from "@/components/rana-page-transition";
import { ProjectFilesList } from "@/features/project-file";
import { ProcessOverview } from "@/features/project-process";
import { ProjectUserList } from "@/features/project-user";
import { PublicationOverview } from "@/features/publications";
import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";
import { useProcessesTabLabel } from "@/hooks/use-processes-tab-label";
import { RanaTabs } from "@/lib/rana-tabs";
import { useProjectQuery } from "@/queries/query-project";
import { ProjectProvider } from "@/stores/project-provider";

import { ProjectHeader } from "./components/project-header";
import { ProjectOverviewOrWelcome } from "./components/project-overview-or-welcome";
import { ProjectSettings } from "./components/project-settings";

const ProjectLoading = () => (
  <RanaLoading
    boxSize={24}
    position="fixed"
    top="1/2"
    left="1/2"
    transform="translate(-50%, -50%)"
  />
);

const ProjectTabWrapper = ({ children }: { children: ReactNode }) => {
  const { permissionsLoading } = useProjectActionAllowed();

  return (
    <RanaPageTransition updateKey={permissionsLoading + ""}>
      {permissionsLoading ? <ProjectLoading /> : children}
    </RanaPageTransition>
  );
};

interface ProjectTabsProps {
  slug: string;
}

const PROJECT_TAB_KEYS = [
  "overview",
  "files",
  "processes",
  "publications",
  "users",
  "settings",
] as const;
export type ProjectTabKey = (typeof PROJECT_TAB_KEYS)[number];

// Panels need to be functions so query params get updated when you switch back and forth
function getProjectTabs(projectName: string, processesLabel: ReactNode) {
  return {
    overview: {
      title: <FormattedMessage id="tab.overview" defaultMessage="Overview" />,
      panel: (
        <ProjectTabWrapper>
          <RanaPageTitle
            projectName={projectName}
            subTitle={
              <FormattedMessage id="page_title.project.overview" defaultMessage="Overview" />
            }
          />
          <ProjectOverviewOrWelcome />
        </ProjectTabWrapper>
      ),
    },
    files: {
      title: <FormattedMessage id="tab.files" defaultMessage="Files" />,
      panel: (
        <ProjectTabWrapper>
          <RanaPageTitle
            projectName={projectName}
            subTitle={<FormattedMessage id="page_title.project.files" defaultMessage="Files" />}
          />
          <ProjectFilesList />
        </ProjectTabWrapper>
      ),
    },
    processes: {
      title: processesLabel,
      panel: (
        <ProjectTabWrapper>
          <RanaPageTitle projectName={projectName} subTitle={processesLabel} />
          <ProcessOverview />
        </ProjectTabWrapper>
      ),
    },
    publications: {
      title: <FormattedMessage id="tab.publications" defaultMessage="Publications" />,
      panel: (
        <ProjectTabWrapper>
          <RanaPageTitle
            subTitle={
              <FormattedMessage
                id="page_title.project.publications"
                defaultMessage="Publications"
              />
            }
          />
          <PublicationOverview />
        </ProjectTabWrapper>
      ),
    },
    users: {
      title: <FormattedMessage id="tab.users" defaultMessage="Users" />,
      panel: (
        <ProjectTabWrapper>
          <RanaPageTitle
            projectName={projectName}
            subTitle={<FormattedMessage id="page_title.project.users" defaultMessage="Users" />}
          />
          <ProjectUserList />
        </ProjectTabWrapper>
      ),
    },
    settings: {
      title: <FormattedMessage id="tab.settings" defaultMessage="Settings" />,
      panel: (
        <ProjectTabWrapper>
          <RanaPageTitle
            projectName={projectName}
            subTitle={
              <FormattedMessage id="page_title.project.settings" defaultMessage="Settings" />
            }
          />
          <ProjectSettings />
        </ProjectTabWrapper>
      ),
    },
  } as const satisfies Record<ProjectTabKey, unknown>;
}

export const TAB_KEY = "tab";
// For performance reasons we only use tab=[number]. Therefore, we translate tab=files to tab=1
export const getProjectTabQueryParam = (tab: ProjectTabKey) => {
  const tabIndex = PROJECT_TAB_KEYS.indexOf(tab);
  return `${TAB_KEY}=${tabIndex}`;
};

export function ProjectTabs({ slug }: ProjectTabsProps) {
  const { data: project, isLoading } = useProjectQuery({ slug });
  const processesLabel = useProcessesTabLabel();

  if (isLoading) return <ProjectLoading />;

  if (!project) {
    return null;
  }

  return (
    <ProjectProvider project={project}>
      <ProjectHeader />
      <RanaTabs isPageNav tabs={getProjectTabs(project.name, processesLabel)} tabKey={TAB_KEY} />
    </ProjectProvider>
  );
}
